import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactLoading from 'react-loading';
import AccommodationForm from '../accommodation/AccommodationForm';
import { orgGetAccommodationById } from '../../api/orgAccommodation';
import errorHandle from '../../utils/errorHandle';
import './orgAccommodationUpload.css';

/*
 * Organizer > Camp: create, edit or duplicate a stay
 * (mounted at /org/c/create/camp under OrgCampLayout).
 *
 *   /org/c/create/camp                                → blank create
 *   /org/c/create/camp?new=false&type=edit&id=ID      → edit that stay
 *   /org/c/create/camp?new=false&type=duplicate&id=ID → new stay pre-filled
 *                                                        from that one (ids removed)
 *
 * Same flow as SupportPackUploadWrapper uses for support, but with the
 * organizer's own endpoints (the backend only returns / updates stays that
 * belong to the logged-in organizer).
 */
const LIST_PATH = '/org/c/my-camps';

const OrgAccommodationUpload = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const isExisting = searchParams.get('new') === 'false';
    const isEdit = isExisting && searchParams.get('type') !== 'duplicate';
    const id = searchParams.get('id');

    const [initialData, setInitialData] = useState(null);
    const [loading, setLoading] = useState(isExisting);

    useEffect(() => {
        if (!isExisting || !id) {
            setInitialData(null);
            setLoading(false);
            return undefined;
        }
        let gone = false;
        setLoading(true);
        orgGetAccommodationById(id)
            .then((res) => !gone && setInitialData(res?.data?.data || null))
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [isExisting, id]);

    if (loading) {
        return (
            <div className='loadingBackground'>
                <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
            </div>
        );
    }

    if (isExisting && !initialData) {
        return (
            <div className='orgAccommodationUploadError'>
                <h3>Couldn't load this stay</h3>
                <button type='button' className='blueBtn' onClick={() => navigate(LIST_PATH)}>
                    Back to My Packages
                </button>
            </div>
        );
    }

    return (
        <AccommodationForm
            // remount when switching between create / edit / duplicate
            key={`${isEdit ? 'edit' : 'create'}-${id || 'new'}`}
            mode={isEdit ? 'edit' : 'create'}
            role='organizer'
            accommodationId={isEdit ? id : null}
            initialData={initialData}
            // new stays and edited rejected ones wait for review; edited live stays stay live
            onSuccess={(res) =>
                navigate(`${LIST_PATH}?status=${res?.data?.data?.status === 'active' ? 'active' : 'inactive'}`)
            }
        />
    );
};

export default OrgAccommodationUpload;
